export type TemplateId =
  | 'classic'
  | 'modern'
  | 'compact'
  | 'card'
  | 'banner'
  | 'minimal'
  | 'elegant'
  | 'stacked';

export interface SocialLinks {
  nostr?: string;
  twitter?: string;
  github?: string;
  linkedin?: string;
  telegram?: string;
  instagram?: string;
}

export interface SignatureData {
  name: string;
  title: string;
  business: string;
  logo: string;
  email: string;
  phone: string;
  website: string;
  address: string;
  tagline: string;
  bchAddress: string;
  bchLabel: string;
  bchMessage: string;
  showBchQr: boolean;
  social: SocialLinks;
  showAcceptBadge: boolean;
  showPoweredBadge: boolean;
  accentColor: string;
  bgColor: string;
  textColor: string;
  template: TemplateId;
  /** Opt-in: wrap outgoing links in trackable redirects for click analytics. */
  trackLinks: boolean;
  /** Anonymous, random id for this signature (per-signature click counts). */
  sigId: string;
}

/** Generate a short random anonymous signature id. */
export function generateSigId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export const BCH_GREEN = '#0AC18E';

export const defaultSignature: SignatureData = {
  name: 'Satoshi Nakamoto',
  title: 'Founder & CEO',
  business: 'Acme Cash Co.',
  logo: '',
  email: 'satoshi@acmecash.co',
  phone: '+1 (555) 012-3456',
  website: 'https://acmecash.co',
  address: '21 Freedom Ave, Austin, TX',
  tagline: 'Peer-to-peer electronic cash for everyone.',
  bchAddress: 'bitcoincash:qr95sy3j9xwd2ap32xkykttr4cvcu7as4y0qverfuy',
  bchLabel: '',
  bchMessage: '',
  showBchQr: true,
  social: {
    nostr: '',
    twitter: '',
    github: '',
    linkedin: '',
    telegram: '',
    instagram: '',
  },
  showAcceptBadge: true,
  showPoweredBadge: true,
  accentColor: BCH_GREEN,
  bgColor: '#ffffff',
  textColor: '#111111',
  template: 'modern',
  trackLinks: false,
  sigId: '',
};

export const TEMPLATES: { id: TemplateId; name: string; description: string }[] = [
  { id: 'modern', name: 'Modern', description: 'Logo left, accent divider, badges' },
  { id: 'classic', name: 'Classic', description: 'Clean stacked text, timeless look' },
  { id: 'card', name: 'Card', description: 'Bordered card with QR spotlight' },
  { id: 'banner', name: 'Banner', description: 'Bold accent header bar on top' },
  { id: 'elegant', name: 'Elegant', description: 'Refined serif, centered layout' },
  { id: 'stacked', name: 'Stacked', description: 'Two-tone side accent panel' },
  { id: 'minimal', name: 'Minimal', description: 'Bare text, no icons or borders' },
  { id: 'compact', name: 'Compact', description: 'Single-line minimal footprint' },
];

export interface ColorPreset {
  name: string;
  bg: string;
  text: string;
}

/** Ready-made background/text color combinations. */
export const COLOR_PRESETS: ColorPreset[] = [
  { name: 'White', bg: '#ffffff', text: '#111111' },
  { name: 'Snow', bg: '#f7faf9', text: '#0f2e26' },
  { name: 'Mint', bg: '#eafaf4', text: '#0b3b2e' },
  { name: 'Slate', bg: '#f1f5f9', text: '#1e293b' },
  { name: 'Sand', bg: '#faf6ef', text: '#3a2f21' },
  { name: 'Midnight', bg: '#0f172a', text: '#e2e8f0' },
  { name: 'Charcoal', bg: '#1c1c1e', text: '#f2f2f2' },
  { name: 'Forest', bg: '#0b2e24', text: '#d7f5ea' },
];

/**
 * Normalize a URL for use in an href, ensuring an https(s) protocol.
 * Rejects dangerous schemes (javascript:, data:, etc.) by returning ''.
 */
export function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';
  // If it already carries a scheme, only allow http(s).
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return '';
  }
  return `https://${trimmed}`;
}

/** Strip protocol/trailing slash for display. */
export function displayUrl(url: string): string {
  return url.trim().replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

/* ---------------- Color helpers ---------------- */

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function hexToRgb(hex: string): Rgb {
  let h = hex.replace('#', '').trim();
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const num = parseInt(h || '000000', 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function rgbToHex({ r, g, b }: Rgb): string {
  const to = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

/** Mix two hex colors; amount 0 = a, 1 = b. */
function mix(a: string, b: string, amount: number): string {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  return rgbToHex({
    r: ca.r + (cb.r - ca.r) * amount,
    g: ca.g + (cb.g - ca.g) * amount,
    b: ca.b + (cb.b - ca.b) * amount,
  });
}

/** Relative luminance (0 dark – 1 light). */
export function luminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

export function isDark(hex: string): boolean {
  return luminance(hex) < 0.5;
}

/** A derived color palette for building a signature on any background. */
export interface Palette {
  bg: string;
  text: string;
  muted: string;
  faint: string;
  border: string;
  panel: string;
  accent: string;
  onAccent: string;
  dark: boolean;
}

/**
 * Derive a full palette from the user's chosen background, text and accent.
 * Muted/border/panel colors are mixed toward the opposite tone so they read
 * correctly on both light and dark backgrounds.
 */
export function derivePalette(
  bg: string,
  text: string,
  accent: string,
): Palette {
  const dark = isDark(bg);
  return {
    bg,
    text,
    muted: mix(text, bg, 0.35),
    faint: mix(text, bg, 0.55),
    border: mix(bg, text, dark ? 0.22 : 0.12),
    panel: mix(bg, text, dark ? 0.1 : 0.04),
    accent,
    onAccent: isDark(accent) ? '#ffffff' : '#0a2f24',
    dark,
  };
}

/** Basic HTML entity escaping to keep interpolated text safe. */
export function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Build a BIP-21 payment URI for the given BCH address, attaching optional
 * `label` and `message` parameters. Returns '' if there is no address.
 *
 * Example: bitcoincash:qr95…?label=Acme%20Cash&message=Invoice
 */
export function buildBchUri(data: SignatureData): string {
  const raw = data.bchAddress.trim();
  if (!raw) return '';

  // Normalize to a single `bitcoincash:` prefix.
  const address = raw.replace(/^bitcoincash:/i, '');
  const base = `bitcoincash:${address}`;

  const label = (data.bchLabel ?? '').trim();
  const message = (data.bchMessage ?? '').trim();

  const params = new URLSearchParams();
  if (label) params.set('label', label);
  if (message) params.set('message', message);

  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

/** base64url encode a UTF-8 string (browser-safe). */
export function base64UrlEncode(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  const b64 = btoa(binary);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Wrap a destination URL in a trackable redirect link when tracking is
 * enabled. The destination is encoded in the URL itself (no server-side
 * link database needed), and only aggregate click counts are recorded.
 *
 * @param origin  Absolute origin of the deployed app (e.g. https://sig.example)
 * @param sigId   Optional anonymous signature id for per-signature counts
 */
export function trackableLink(
  destination: string,
  linkType: string,
  origin: string,
  enabled: boolean,
  sigId?: string,
): string {
  if (!enabled || !destination) return destination;
  if (!origin) return destination;
  const u = base64UrlEncode(destination);
  const params = new URLSearchParams({ u, t: linkType });
  if (sigId) params.set('s', sigId);
  return `${origin.replace(/\/$/, '')}/.netlify/functions/r?${params.toString()}`;
}

/** Build a nostr profile URL from an npub or nostr: uri. */
export function nostrUrl(value: string): string {
  const v = value.trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  const npub = v.replace(/^nostr:/i, '');
  return `https://njump.me/${npub}`;
}
